/**
 * Offline Context
 * Provides offline state and capabilities to all components
 * - Manages connection status
 * - Provides offline-first API
 * - Handles sync operations
 * - Shows conflicts for resolution
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createOfflineAPI, checkServerConnection, onConnectionChange, getConnectionState } from '../utils/offlineAPI';
import { startSync, onSyncProgress, getSyncStatus, forceFullRefresh } from '../utils/syncEngine';
import offlineDB, { 
  getSyncQueueCount, 
  getUnresolvedConflicts,
  getLastSyncTime,
  initOfflineDB 
} from '../utils/offlineDB';
import { useAuth } from './AuthContext';

const OfflineContext = createContext(null);

export const OfflineProvider = ({ children }) => {
  const { token, user } = useAuth();
  
  // Connection state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isServerReachable, setIsServerReachable] = useState(true);
  const [isFullyOnline, setIsFullyOnline] = useState(true);
  
  // Sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ total: 0, completed: 0, failed: 0, conflicts: 0 });
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Conflicts
  const [conflicts, setConflicts] = useState([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  
  // Offline API instance
  const offlineAPI = useRef(null);

  // Initialize
  useEffect(() => {
    const init = async () => {
      await initOfflineDB();
      await updatePendingCount();
      await updateConflicts();
      const lastSync = await getLastSyncTime();
      setLastSyncTime(lastSync);
    };
    init();
  }, []);

  // Create offline API instance
  useEffect(() => {
    offlineAPI.current = createOfflineAPI(() => token);
  }, [token]);

  // Subscribe to connection changes
  useEffect(() => {
    const unsubscribe = onConnectionChange((state) => {
      setIsOnline(state.isOnline);
      setIsServerReachable(state.isServerReachable);
      setIsFullyOnline(state.isFullyOnline);
      
      // Auto-sync when coming back online
      if (state.isFullyOnline && pendingCount > 0 && !isSyncing) {
        triggerSync();
      }
    });

    return unsubscribe;
  }, [pendingCount, isSyncing]);

  // Subscribe to sync progress
  useEffect(() => {
    const unsubscribe = onSyncProgress((progress) => {
      setIsSyncing(progress.isSyncing);
      setSyncProgress(progress);
      
      if (!progress.isSyncing) {
        updatePendingCount();
        updateConflicts();
      }
    });

    return unsubscribe;
  }, []);

  // Periodic connection check
  useEffect(() => {
    const interval = setInterval(async () => {
      if (navigator.onLine) {
        await checkServerConnection();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Update pending count
  const updatePendingCount = async () => {
    try {
      const count = await getSyncQueueCount();
      setPendingCount(count);
    } catch (error) {
      console.error('Failed to get pending count:', error);
    }
  };

  // Update conflicts
  const updateConflicts = async () => {
    try {
      const unresolvedConflicts = await getUnresolvedConflicts();
      setConflicts(unresolvedConflicts);
      
      // Show dialog if there are conflicts
      if (unresolvedConflicts.length > 0) {
        setShowConflictDialog(true);
      }
    } catch (error) {
      console.error('Failed to get conflicts:', error);
    }
  };

  // Trigger sync
  const triggerSync = useCallback(async () => {
    if (!token || isSyncing) return;
    
    try {
      const result = await startSync(token);
      const lastSync = await getLastSyncTime();
      setLastSyncTime(lastSync);
      return result;
    } catch (error) {
      console.error('Sync failed:', error);
      return { success: false, error: error.message };
    }
  }, [token, isSyncing]);

  // Force full refresh
  const refreshAllData = useCallback(async (restaurantId) => {
    if (!token) return;
    
    try {
      const result = await forceFullRefresh(restaurantId, token);
      const lastSync = await getLastSyncTime();
      setLastSyncTime(lastSync);
      return result;
    } catch (error) {
      console.error('Full refresh failed:', error);
      return { success: false, error: error.message };
    }
  }, [token]);

  // Get the offline-enabled API
  const getAPI = useCallback(() => {
    if (!offlineAPI.current) {
      offlineAPI.current = createOfflineAPI(() => token);
    }
    return offlineAPI.current;
  }, [token]);

  const value = {
    // Connection state
    isOnline,
    isServerReachable,
    isFullyOnline,
    isOffline: !isOnline || !isServerReachable,
    
    // Sync state
    isSyncing,
    syncProgress,
    pendingCount,
    lastSyncTime,
    
    // Conflicts
    conflicts,
    hasConflicts: conflicts.length > 0,
    showConflictDialog,
    setShowConflictDialog,
    
    // Actions
    triggerSync,
    refreshAllData,
    updatePendingCount,
    updateConflicts,
    checkConnection: checkServerConnection,
    
    // API
    api: getAPI(),
    getAPI
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export default OfflineContext;
